<template>
    <div class="worktop-view view" :class="{ incomplete: !dimensionsFilled }">
        <instructions titleKey="worktop" :property="state.productFamily == 'seven' ? 'worktopHelpContent' : 'worktopHelpContentOne'" />

        <worktop-drawing :editableFields="[ 'worktopLengthInt', 'worktopHeightInt', 'worktopWidthInt' ]" />

        <div class="wrapper">
            <div class="section-content">
                <div class="row">
                    <div class="section-main col-6">
                        <h2 class="h2" v-html="ui.t('worktopDimensionsTitle')"></h2>

                        <div class="row">
                            <div class="col-12 worktop-length">
                                <number-input v-model="worktopLength"
                                    resourceKey="worktopLength"
                                    @blur="ui.clearFocusedField()"
                                    @focus="ui.focusedFieldName = 'worktopLength'"
                                    :step="5"
                                    :min="300"
                                    :max="3000" />
                            </div>
                        </div>
                        <div class="row">
                            <div class="col-12">
                                <option-list v-model="worktopWidth"
                                    :optionList="ui.worktopWidths"
                                    classes="big"
                                    @blur="ui.clearFocusedField()"
                                    @focus="ui.focusedFieldName = 'worktopWidth'" />

                                <option-list v-model="worktopHeight"
                                    :optionList="ui.worktopHeights"
                                    classes="big"
                                    @blur="ui.clearFocusedField()"
                                    @focus="ui.focusedFieldName = 'worktopHeight'" />
                            </div>
                        </div>
                    </div>

                    <div class="section-side col-3">
                        <h2 class="h2 selection-title" :class="{ collapsed: edgeProfileCollapsed }" v-html="ui.t('worktopEdgeProfileTitle')"></h2>
                        <option-list v-model="state.edgeProfileCode"
                            :optionList="ui.edgeProfiles"
                            :withImages="true"
                            :itemClasses="[ 'col-6' ]"
                            @selected="(v: string) => edgeProfileCollapsed = !!v" />


                        <h2 class="h2 selection-title" :class="{ collapsed: worktopLocationCollapsed }" v-html="ui.t('worktopLocationTitle')" v-if="!state.disabledWorktopLocation"></h2>
                        <option-list v-model="state.worktopLocation" 
                            :optionList="ui.worktopLocations" 
                            :withImages="true"
                            :itemClasses="[ 'col-6' ]"
                            @selected="(v: string) => worktopLocationCollapsed = !!v"
                            v-if="!state.disabledWorktopLocation" />
                    </div>
                    
                    <div class="section-side col-3">
                        <h2 class="h2 selection-title" :class="{ collapsed: backStyleCollapsed }" v-html="ui.t('worktopBackStyleTitle')" v-if="!state.disabledBackStyle"></h2>
                        <div class="wrapper-relative">
                            <span class="input-tip warning" v-if="!state.restrictRaisedBackOnIsland && state.backStyle == 'BackRaised'" v-html="ui.t('worktopBackStyleIslandNotRecommended')"></span>
                            <span class="input-tip info" v-if="showRaisedBackNoticePopup">
                                {{ ui.t("raisedBackInfoPopup") }}
                            </span> 
                            <option-list v-model="state.backStyle"
                                :optionList="ui.worktopBackStyles"
                                :withImages="true"
                                :itemClasses="[ 'col-6' ]"
                                :valueChangedText="ui.t('raisedBackRemoved')"
                                :disabledInfoText="backStyleDisableText"
                                @selected="(v: string) => backStyleCollapsed = !!v"
                                @item-clicked="handleRaisedBackNotice"
                                v-if="!state.disabledBackStyle" />
                        </div>
                    </div>
                </div>
            </div>
        </div>

        <div class="navigation">
            <div class="wrapper">
                <a href="" @click.prevent="" class="disabled" v-html="ui.t('previous')"></a>
                <router-link :to="`/${state.productFamilyCode}/material`" @click.native="moveNext" :class="{next: true, disabled: !ui.worktopSelectionsComplete }" >
                    <span class="input-tip" v-if="moveNextPrevented" v-html="ui.t('selectionsIncomplete')"></span>
                    <span v-html="ui.t('next')"></span>
                </router-link>
            </div>
        </div>
    </div>
</template>

<style lang="less" scoped>
    .notification-container {
        position: relative;
    }
</style>

<script async setup lang="ts">
import Collapse from "@/ts/components/Collapse.vue";
import { computed, ref, watch, type Ref } from 'vue';
import { useState, useUI } from '../store';
import Popup from "../components/Popup.vue";
import type { ConfigurationOption, Option } from "../models";

const moveNextPrevented = ref(false);
let moveNextTimer: number | null = null;

const { ui } = useUI();
const { state } = useState();

const edgeProfileCollapsed = ref(false);
const worktopLocationCollapsed = ref(false);
const backStyleCollapsed = ref(false);
const showRaisedBackNoticePopup = ref(false);

let raisedBackNoticePopupTimeoutHandle: number | undefined;

function mounted() {
    state.mode = "worktop";
}

function moveNext() {
    moveNextPrevented.value = true;

    moveNextTimer = setTimeout(() => moveNextPrevented.value = false, 3000);
}

function handleRaisedBackNotice(evt: { value: Option }) {
    if (evt.value.value != "BackRaised" || !state.restrictRaisedBackOnIsland)
        return;
    
    showRaisedBackNoticePopup.value = true;
    clearTimeout(raisedBackNoticePopupTimeoutHandle);
    raisedBackNoticePopupTimeoutHandle = setTimeout(() => {
        showRaisedBackNoticePopup.value = false;
    }, 4_000);
}

const backStyleDisableText = computed(() => {
    
    if (state.material == "F") {
        return ui.t('backStyleNotAvailableWithMaterial');
    }

    return state.restrictRaisedBackOnIsland ? ui.t('worktopBackStyleNotIslandRequired') : ui.t('worktopBackStyleIslandNotRecommended')
});

const dimensionsFilled = computed(() => {
    let dimensions = [ state.worktopLength, state.worktopHeight, state.worktopWidth ];

    return dimensions.filter(d => d).length == dimensions.length;
});

const worktopLength = computed({
    get() {
        return ui.worktopLength
    },
    set(value: number) {
        ui.worktopLength = value
    }
});

const worktopWidth = computed({
    get() {
        return state.worktopWidth
    },
    set(value: string) {
        state.worktopWidth = value
    }
});

const worktopHeight = computed({
    get() {
        return state.worktopHeight
    },
    set(value: string) {
        state.worktopHeight = value
    }
});

mounted();

</script>